import { __rest } from '../_virtual/_tslib.js';
import { isIOSWebKit } from './browser.js';

/**
 * Keyboard viewport compensation for browsers that do not resize/scroll correctly
 * when the soft keyboard opens (primarily iOS Safari inside locked-height sheets).
 *
 * Android Chrome resizes the viewport and scrolls focused inputs natively — every compose
 * helper below is a no-op there so we never fight the browser. That relies on the sheet's
 * viewport meta keeping `interactive-widget=resizes-content` (see methods/dom.ts); with
 * `overlays-content` nothing resizes and the native scroll silently stops happening.
 *
 * Layers: policy → measure → act → schedule → compose.
 */
// --- policy ------------------------------------------------------------------
/**
 * True only where the browser cannot keep focused fields / menus above the keyboard.
 * Gate all custom scroll + keyboard menu logic through this.
 */
function needsKeyboardViewportCompensation() {
    return isIOSWebKit();
}
// --- sheet scroll root -------------------------------------------------------
/** Marks the scroll container inside a bottom sheet / modal body (nested scrollport). */
const SHEET_SCROLL_ROOT_ATTR = "data-sheet-scroll-root";
const SHEET_SCROLL_ROOT_SELECTOR = `[${SHEET_SCROLL_ROOT_ATTR}]`;
const POPUP_BODY_SELECTOR = "[data-popup-body]";
/** React props for a sheet/modal nested scroll root element. */
function getSheetScrollRootProps() {
    return { [SHEET_SCROLL_ROOT_ATTR]: "" };
}
// --- iOS clearance / timing --------------------------------------------------
/** Gap between focused field bottom and keyboard top (iOS only). */
const FIELD_CLEARANCE_PX = 8;
const MENU_MIN_SPACE_PX = 120;
const OBSERVE_MS = 900;
const OBSERVE_DEBOUNCE_MS = 100;
const SETTLE_MS = 300;
function getKeyboardViewportClearance(extraPx = 0) {
    return FIELD_CLEARANCE_PX + extraPx;
}
function resolveClearance(clearance) {
    return clearance !== null && clearance !== void 0 ? clearance : getKeyboardViewportClearance();
}
/** Visible area above the keyboard, in layout-viewport coordinates. */
function getKeyboardViewportRect() {
    const viewport = typeof window === "undefined" ? null : window.visualViewport;
    if (!viewport) {
        const height = typeof window === "undefined" ? 0 : window.innerHeight;
        return { top: 0, bottom: height, height };
    }
    return {
        top: viewport.offsetTop,
        bottom: viewport.offsetTop + viewport.height,
        height: viewport.height
    };
}
function getKeyboardViewportSpace(element, clearance) {
    const inset = resolveClearance(clearance);
    const rect = element.getBoundingClientRect();
    const view = getKeyboardViewportRect();
    return {
        above: Math.max(0, rect.top - (view.top + inset)),
        below: Math.max(0, view.bottom - inset - rect.bottom),
        gap: view.bottom - rect.bottom
    };
}
function getKeyboardScrollContainer(element) {
    const scrollRoot = element.closest(SHEET_SCROLL_ROOT_SELECTOR);
    if (scrollRoot instanceof HTMLElement) {
        return scrollRoot;
    }
    const popupBody = element.closest(POPUP_BODY_SELECTOR);
    if (popupBody instanceof HTMLElement) {
        return popupBody;
    }
    return null;
}
/** Smallest scroll that brings the element fully inside the visible area. */
function getKeyboardScrollDelta(element, clearance) {
    const rect = element.getBoundingClientRect();
    const view = getKeyboardViewportRect();
    if (rect.bottom > view.bottom - clearance) {
        return rect.bottom - (view.bottom - clearance);
    }
    if (rect.top < view.top + clearance) {
        return rect.top - (view.top + clearance);
    }
    return 0;
}
// --- act ---------------------------------------------------------------------
/**
 * Single, minimal scroll pass — no-ops when the element already clears the keyboard,
 * and no-ops entirely when the browser handles the keyboard natively (Android).
 */
function scrollIntoKeyboardView(element, { clearance } = {}) {
    if (!needsKeyboardViewportCompensation()) {
        return;
    }
    const delta = getKeyboardScrollDelta(element, resolveClearance(clearance));
    if (Math.abs(delta) < 1) {
        return;
    }
    const container = getKeyboardScrollContainer(element);
    if (container) {
        container.scrollTop += delta;
        return;
    }
    window.scrollBy({ top: delta, behavior: "auto" });
}
/**
 * Picks dropdown side from space in the visible viewport.
 * react-select `auto` measures the layout viewport — wrong inside locked-height sheets.
 */
function resolveKeyboardMenuPlacement(element, { maxHeight, minSpace = MENU_MIN_SPACE_PX, clearance }) {
    const { above, below } = getKeyboardViewportSpace(element, clearance);
    const required = Math.min(maxHeight, minSpace);
    if (below >= required) {
        return "bottom";
    }
    if (above >= required) {
        return "top";
    }
    return below >= above ? "bottom" : "top";
}
/**
 * Body-portaled `position: fixed` menus drift on iOS when the keyboard scrolls the
 * control. Prefer absolute attach there; Android portals are fine.
 */
function shouldPortalSelectMenuToBody() {
    return !needsKeyboardViewportCompensation();
}
/** Resolves react-select menu attach props from a portal preference. */
function getSelectMenuAttachConfig(preferPortalToBody) {
    if (preferPortalToBody && shouldPortalSelectMenuToBody()) {
        return {
            menuPosition: "fixed",
            menuPortalTarget: typeof document === "undefined" ? undefined : document.body
        };
    }
    return { menuPosition: "absolute" };
}
/** Debounced `visualViewport` listener that expires on its own. Returns an unsubscribe. */
function observeKeyboardViewport(onChange, { durationMs = OBSERVE_MS, debounceMs = OBSERVE_DEBOUNCE_MS } = {}) {
    const viewport = typeof window === "undefined" ? null : window.visualViewport;
    if (!viewport) {
        return () => { };
    }
    let debounceId = null;
    let expiryId = null;
    const stop = () => {
        if (debounceId !== null) {
            window.clearTimeout(debounceId);
            debounceId = null;
        }
        if (expiryId !== null) {
            window.clearTimeout(expiryId);
            expiryId = null;
        }
        viewport.removeEventListener("resize", handleChange);
        viewport.removeEventListener("scroll", handleChange);
    };
    function handleChange() {
        if (debounceId !== null) {
            window.clearTimeout(debounceId);
        }
        debounceId = window.setTimeout(onChange, debounceMs);
    }
    viewport.addEventListener("resize", handleChange);
    viewport.addEventListener("scroll", handleChange);
    expiryId = window.setTimeout(stop, durationMs);
    return stop;
}
/** Keyboard animation duration — measuring before this returns pre-keyboard geometry. */
function getKeyboardSettleMs() {
    return SETTLE_MS;
}
/**
 * Runs `apply` once the keyboard has settled, then again on each viewport change
 * until it stops moving. No-ops when compensation is not needed. `apply` must be idempotent.
 */
function onKeyboardSettle(apply, { settleMs = getKeyboardSettleMs(), observeMs } = {}) {
    if (!needsKeyboardViewportCompensation()) {
        return () => { };
    }
    const settleId = window.setTimeout(apply, settleMs);
    const stopObserving = observeKeyboardViewport(apply, {
        durationMs: observeMs
    });
    return () => {
        window.clearTimeout(settleId);
        stopObserving();
    };
}
// --- compose -----------------------------------------------------------------
/**
 * Newest pass wins. Only one element holds focus at a time, so only one pass is ever
 * relevant, and callers wire this to focus events and drop the disposer. Without this,
 * moving between fields — every digit of an OTP row — leaves the previous pass running
 * with its own visualViewport listeners, and they scroll against each other.
 */
let disposeActiveKeyboardPass = null;
/** Keeps a focused element above the keyboard. No-op on Android (native handles it). */
function keepInKeyboardView(element, _a = {}) {
    var { clearance } = _a, settle = __rest(_a, ["clearance"]);
    if (!needsKeyboardViewportCompensation()) {
        return () => { };
    }
    disposeActiveKeyboardPass === null || disposeActiveKeyboardPass === void 0 ? void 0 : disposeActiveKeyboardPass();
    const stopSettling = onKeyboardSettle(() => scrollIntoKeyboardView(element, { clearance }), settle);
    const dispose = () => {
        stopSettling();
        if (disposeActiveKeyboardPass === dispose) {
            disposeActiveKeyboardPass = null;
        }
    };
    disposeActiveKeyboardPass = dispose;
    return dispose;
}
function resolveFocusTarget(candidateIds) {
    for (const id of candidateIds) {
        const element = document.getElementById(id);
        if (!element) {
            continue;
        }
        if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
            return element;
        }
        const nestedInput = element.querySelector('input:not([type="hidden"])');
        if (nestedInput) {
            return nestedInput;
        }
    }
    return null;
}
/**
 * Focuses an element without letting the browser's scroll-into-view fight ours.
 * Native scroll is suppressed only where it is known to be wrong (iOS); elsewhere it runs
 * first and our settled pass corrects it only if the keyboard still covers the field.
 */
function focusKeyboardAware(element, { scrollIntoView = false, clearance } = {}) {
    element.focus({
        preventScroll: scrollIntoView && needsKeyboardViewportCompensation()
    });
    if (scrollIntoView) {
        keepInKeyboardView(element, { clearance });
    }
}
/** Focuses the first resolvable control by id, then keeps it above the keyboard. */
function focusFormControl({ candidateIds, delay = 0, scrollIntoView = false, clearance, maxRetries = needsKeyboardViewportCompensation() ? 2 : 1, retryIntervalMs = 120 }) {
    const attemptFocus = (attempt = 0) => {
        const focusTarget = resolveFocusTarget(candidateIds);
        if (!focusTarget) {
            if (attempt < maxRetries) {
                window.setTimeout(() => attemptFocus(attempt + 1), retryIntervalMs);
            }
            return;
        }
        focusKeyboardAware(focusTarget, { scrollIntoView, clearance });
    };
    if (delay > 0) {
        window.setTimeout(attemptFocus, delay);
        return;
    }
    requestAnimationFrame(attemptFocus);
}

export { SHEET_SCROLL_ROOT_ATTR, SHEET_SCROLL_ROOT_SELECTOR, focusFormControl, focusKeyboardAware, getKeyboardSettleMs, getKeyboardViewportClearance, getKeyboardViewportRect, getKeyboardViewportSpace, getSelectMenuAttachConfig, getSheetScrollRootProps, keepInKeyboardView, needsKeyboardViewportCompensation, observeKeyboardViewport, onKeyboardSettle, resolveKeyboardMenuPlacement, scrollIntoKeyboardView, shouldPortalSelectMenuToBody };
//# sourceMappingURL=keyboardViewport.js.map
